import {api, BASE_URL} from "../config";
import { getCurrentLanguage } from "../i18n"; // adapte le chemin
import { openAuthenticatedEventSource } from "../utils/openAuthenticatedEventSource";
import { authFetch } from "../utils/authFetch";
import { openVersionComparisonStream } from './versionComparison.service';
import {
    getComparisonMetadata,
    isComparisonChangeComment,
    isComparisonRiskComment,
    normalizeComparisonCommentType,
} from "../utils/comparisonRisk";

const IA_URL = BASE_URL + "ia-review-results/";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertUuid(value, fieldName) {
    if (!value || !UUID_RE.test(String(value))) {
        throw new Error(`${fieldName}_invalid`);
    }
}

export async function getLatestReferenceReviewSnapshot(analyseId, documentVersionId) {
    assertUuid(analyseId, "analyse_id");
    assertUuid(documentVersionId, "document_version_id");
    const params = new URLSearchParams();
    params.set("document_version_id", documentVersionId);
    const response = await authFetch(
        `${BASE_URL}analyses/${analyseId}/reference-review-snapshots/latest?${params.toString()}`,
        {headers: {Accept: "application/json"}},
    );
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`reference_review_snapshot_${response.status}`);
    return response.json();
}

export async function getReferenceReviewViewerSource({
    analyseId,
    documentVersionId,
    referenceFileId,
    reviewId,
    snapshotId = null,
    signal,
}) {
    assertUuid(analyseId, "analyse_id");
    assertUuid(documentVersionId, "document_version_id");
    assertUuid(referenceFileId, "reference_file_id");
    assertUuid(reviewId, "review_id");
    if (snapshotId) assertUuid(snapshotId, "snapshot_id");
    const params = new URLSearchParams({
        document_version_id: documentVersionId,
        reference_file_id: referenceFileId,
        review_id: reviewId,
    });
    if (snapshotId) params.set("snapshot_id", snapshotId);
    const response = await authFetch(
        `${BASE_URL}analyses/${analyseId}/reference-review-viewer-source?${params.toString()}`,
        {headers: {Accept: "application/json"}, signal},
    );
    if (!response.ok) throw new Error(`reference_review_viewer_source_${response.status}`);
    return response.json();
}


export function launchAnalysisStream(analyseId, selectedCriteria, id_doc, onMessage, onError, launchOptions = {}) {
    assertUuid(analyseId, "analyse_id");
    assertUuid(id_doc, "document_version_id");

    const url = `${BASE_URL}analyses/${analyseId}/${id_doc}/launch-stream`;
    const language = getCurrentLanguage();

    // Sérialiser et encoder
    const params = new URLSearchParams({
        selected_criteria: JSON.stringify(selectedCriteria),
        commercial_refusals_as_sse: "true",
    });
    if (launchOptions.accepted_partial_analysis) {
        params.set("accepted_partial_analysis", "true");
    }
    if (launchOptions.accepted_gate_code) {
        params.set("accepted_gate_code", String(launchOptions.accepted_gate_code));
    }
    if (launchOptions.accepted_at) {
        params.set("accepted_at", String(launchOptions.accepted_at));
    }

    if (selectedCriteria.some(selection => selection?.reference_type === 'document_version')) {
        return openVersionComparisonStream(`${url}?${params.toString()}`, {
            'X-Client-Language': getCurrentLanguage(),
            Accept: 'text/event-stream',
        }, onMessage, onError);
    }
    const es = openAuthenticatedEventSource(`${url}?${params.toString()}`, {
        headers: {
            'X-Client-Language': language,
            Accept: "text/event-stream"
        },
    });

    let terminalReceived = false;

    const handleEvent = (event) => {
        const rawData = typeof event?.data === "string" ? event.data.trim() : "";
        if (!rawData || rawData === "[DONE]" || rawData === "undefined" || rawData === "null") {
            return;
        }

        let payload;
        try {
            payload = JSON.parse(rawData);
        } catch (e) {
            console.error("SSE JSON parse error", e);
            return;
        }

        if (!payload || typeof payload !== "object" || Array.isArray(payload)) return;

        if (!payload.event && event.type && event.type !== "message") {
            payload.event = event.type;
        }
        if (["analysis_done", "comparison_terminal", "commercial_refusal"].includes(payload.event)) {
            terminalReceived = true;
            es.close();
        }
        onMessage(payload);
    };

    let transportErrorEmitted = false;
    const handleTransportError = (event) => {
        if (transportErrorEmitted || terminalReceived) return;
        transportErrorEmitted = true;
        // EventSource reconnects automatically after transport errors. A launch URL
        // has side effects, so stop it before notifying the caller.
        es.close();
        if (typeof onError === "function") {
            onError({
                reason: "backend_stream_transport_error",
                message: event?.error?.message || event?.message || null,
                retryable: true,
                status: event?.status || event?.statusCode || null,
                event,
            });
        }
    };

    es.onmessage = handleEvent;
    es.onerror = handleTransportError;
    [
        "routing_start",
        "routing_item",
        "routing_end",
        "routing_error",
        "analysis_context",
        "analysis_started",
        "analysis_observation",
        "analysis_thinking",
        "analysis_activity_note",
        "analysis_mechanism_progress",
        "criterion_started",
        "criterion_cache_hit",
        "criterion_completed",
        "criterion_failed",
        "criterion_error",
        "cross_criterion_consolidation_started",
        "commercial_refusal",
        "comment_card",
        "card_preview",
        "card_enrichment",
        "analysis_reasoning_delta",
        "warning",
        "analysis_done",
        "comparison_started",
        "comparison_progress",
        "comparison_candidate_detected",
        "comparison_card",
        "comparison_terminal",
    ].forEach((eventName) => {
        es.addEventListener(eventName, handleEvent);
    });
    return es;
}


export const flushAnalyseVersion = async (analyseId, documentVersionId) => {
    try {
        assertUuid(analyseId, "analyse_id");
        assertUuid(documentVersionId, "document_version_id");
        const res = await api.post(`/analyse-criteria/${analyseId}/flush/${documentVersionId}`);
        return res.data;
    } catch (error) {
        throw error;
    }
};


export function getIAReviewResults(analyseId, documentVersionId) {
    assertUuid(analyseId, "analyse_id");
    assertUuid(documentVersionId, "document_version_id");

    return api.get(`${IA_URL}analysis/${analyseId}/document/${documentVersionId}`)
        .then(res => res.data) // SuccessResponse avec un tableau de IAReviewResultResponse
        .catch(error => {
            throw error.response?.data || error;
        });
}
export function finalizeAfterStream(
    analyseId,
    documentVersionId,
    rawComments,
    finalizeReason = "terminal_event_received",
    options = {}
) {
    assertUuid(analyseId, "analyse_id");
    assertUuid(documentVersionId, "document_version_id");

    const normalizeQuickViewDescription = (value) => {
        const text = String(value ?? '')
            .replace(/\s+/g, ' ')
            .trim();

        if (!text) return '';

        const withoutEllipsis = text.replace(/(?:\.\.\.|\u2026)+\s*$/u, '').trim();
        if (!withoutEllipsis) return '';

        return /[.!?]$/.test(withoutEllipsis)
            ? withoutEllipsis
            : `${withoutEllipsis}.`;
    };

    const normalizeRiskLevel = (risk) => {
        const raw = risk && typeof risk === "object"
            ? risk.level || risk.risk || risk.severity
            : risk;
        const value = String(raw ?? '').trim().toLowerCase();
        const aliases = {
            critique: "critical",
            eleve: "high",
            "\u00e9lev\u00e9": "high",
            haute: "high",
            moyen: "medium",
            moyenne: "medium",
            faible: "low",
        };
        return aliases[value] || value || null;
    };

    const getRiskRationale = (c) => {
        const risk = c?.risk;
        const fromRisk = risk && typeof risk === "object"
            ? risk.rationale || risk.reason || risk.why
            : null;
        const fromQuickView = c?.quick_view && typeof c.quick_view === "object"
            ? c.quick_view.risk_rationale || c.quick_view.riskRationale
            : null;
        const text = String(fromRisk || c?.risk_rationale || c?.riskReason || fromQuickView || '')
            .replace(/\s+/g, ' ')
            .trim();
        return text || null;
    };

    const normalizeQuickView = (quickView, riskRationale = null) => {
        if (!quickView || typeof quickView !== "object") return quickView ?? null;

        const next = { ...quickView };
        const risk = String(next.risk ?? '').trim();
        const action = String(next.action ?? '').trim();
        const rawDescription = next.description;

        let description = normalizeQuickViewDescription(rawDescription);
        if (!description) {
            const merged = [risk, action ? `Action: ${action}` : '']
                .filter(Boolean)
                .join('. ')
                .trim();
            description = normalizeQuickViewDescription(merged);
        }

        if (description) {
            next.description = description;
        }
        if (riskRationale && !next.risk_rationale) {
            next.risk_rationale = riskRationale;
        }

        return next;
    };
    const mergedById = new Map();
    const withoutId = [];

    for (const c of (rawComments || [])) {
        if (c?.id) {
            const previous = mergedById.get(c.id) || {};
            mergedById.set(c.id, { ...previous, ...c });
        } else {
            withoutId.push(c);
        }
    }

    const deduped = [...mergedById.values(), ...withoutId];

    const comments = deduped
        .filter(c => c && (c.criterion_id || c.criterion) && c.title)
        .map(c => {
        const firstHighlightBbox = Array.isArray(c.highlights)
            ? c.highlights.find((h) => h?.bbox)?.bbox
            : null;
        const bbox = c.bbox || firstHighlightBbox || null;
        const metadataJson = getComparisonMetadata(c);
        const commentType = normalizeComparisonCommentType({
            ...c,
            metadata_json: metadataJson,
        }) || "risk";
        const isComparisonChange = isComparisonChangeComment({
            ...c,
            comment_type: commentType,
        });
        const riskRationale = isComparisonChange ? null : getRiskRationale(c);
        const riskLevel = isComparisonChange ? null : normalizeRiskLevel(c.risk ?? c.quick_view?.risk);
        const isComparisonRisk = isComparisonRiskComment({
            ...c,
            comment_type: commentType,
            metadata_json: metadataJson,
        });
        const resolvedRef = c.ref ?? c.target_paragraph_id ?? (isComparisonRisk ? "" : "global");

        return ({
        // page can be null when no precise highlight exists yet
        page: c.page ?? c.highlights?.[0]?.page ?? null,

        ref: resolvedRef,
        target_paragraph_id: c.target_paragraph_id ?? c.targetParagraphId ?? null,
        target_block_ids: c.target_block_ids ?? c.targetBlockIds ?? null,
        section_id: c.section_id ?? c.sectionId ?? null,
        highlights: c.highlights ?? null,
        highlight_source: c.highlight_source ?? null,
        highlight_confidence: c.highlight_confidence ?? null,
        criterion_id: c.criterion_id || c.criterion,

        title: c.title,
        comment_type: commentType,

        created_at:
            typeof c.date === "string"
                ? c.date.slice(0, 10)
                : new Date().toISOString().slice(0, 10),

        quick_view: normalizeQuickView(c.quick_view, riskRationale),
        detailed_view: c.detailed_view ?? null,
        priority: c.priority ?? null,
        legal_signals: c.legal_signals ?? null,
        metadata_json: metadataJson,

        quote: c.quote ?? null,
        doc_ref: c.doc_ref ?? null,
        file_id: c.file_id ?? null,
        risk: riskLevel,

        bbox_left: bbox?.left ?? null,
        bbox_top: bbox?.top ?? null,
        bbox_width: bbox?.width ?? null,
        bbox_height: bbox?.height ?? null,
        });
    });

    const reservationId = options.reservation_id || options.reservationId || null;
    const payload = {
        analyse_id: analyseId,
        document_version_id: documentVersionId,
        finalize_reason: finalizeReason,
        comments,
    };
    if (reservationId) {
        payload.reservation_id = reservationId;
    }
    if (options.analysis_status) {
        payload.analysis_status = options.analysis_status;
    }
    if (options.coverage) {
        payload.coverage = options.coverage;
    }
    if (Object.prototype.hasOwnProperty.call(options, "partial_analysis")) {
        payload.partial_analysis = options.partial_analysis;
    }
    if (options.finalized_at) {
        payload.finalized_at = options.finalized_at;
    }

    return api.post(
        `${BASE_URL}analyses/${analyseId}/${documentVersionId}/finalize-after-stream`,
        payload
    )
        .then(res => res.data)           // SuccessResponse<LaunchIAWithCommentsResponse>
        .catch(error => {
            throw error.response?.data || error;
        });
}

export async function generate_summary_from_comments(summaryRequest) {
    const lang = localStorage.getItem('lang') || 'en';
    const payload = {
        ...summaryRequest,
        language: summaryRequest?.language || lang,
    };

    const response = await authFetch(
        `${BASE_URL}ia-review-results/stream-summary`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Client-Language': lang
            },
            body: JSON.stringify(payload)
        }
    );

    if (!response.ok) {
        let detail = response.statusText;
        try {
            const data = await response.clone().json();
            detail = data?.detail || data?.message || data?.error || detail;
        } catch (e) {
            try {
                detail = await response.clone().text();
            } catch (_) {
                // keep status text
            }
        }
        throw new Error(detail || `summary_stream_failed:${response.status}`);
    }

    return response;
}
